import React from 'react';
import styles from './HomeDecor.module.css';

/** Full-bleed decorative layer for the hero (replaces photo backgrounds). */
export function HeroBackdrop() {
  return (
    <div className={styles.heroBackdrop} aria-hidden="true">
      <div className={styles.heroGlowLayer} />
      <div className={styles.heroMesh} />
      <svg
        className={styles.heroSvg}
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="heroOrb" cx="50%" cy="38%" r="72%">
            <stop offset="0%" stopColor="#edc917" stopOpacity="0.18" />
            <stop offset="40%" stopColor="#edc917" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="heroOrb2" cx="88%" cy="82%" r="45%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.07" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="heroArc" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="heroRing" x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#edc917" stopOpacity="0" />
            <stop offset="50%" stopColor="#edc917" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#edc917" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect width="1200" height="800" fill="url(#heroOrb)" />
        <rect width="1200" height="800" fill="url(#heroOrb2)" />
        <circle cx="900" cy="220" r="180" fill="none" stroke="url(#heroRing)" strokeWidth="1.2" opacity="0.9" />
        <circle cx="900" cy="220" r="240" fill="none" stroke="#fff" strokeOpacity="0.04" strokeWidth="1" />
        <path
          d="M-40 520 Q 280 380 520 480 T 980 420 T 1280 360"
          fill="none"
          stroke="url(#heroArc)"
          strokeWidth="1.5"
        />
        <path
          d="M-20 600 Q 320 460 600 560 T 1100 500"
          fill="none"
          stroke="#fff"
          strokeOpacity="0.08"
          strokeWidth="1"
        />
        <path
          d="M80 120 L 1120 120 M80 200 L 1120 200 M80 680 L 1120 680"
          stroke="#fff"
          strokeOpacity="0.04"
          strokeWidth="1"
        />
        <g fill="#fff" fillOpacity="0.085">
          {Array.from({ length: 12 * 10 }, (_, k) => {
            const i = Math.floor(k / 10);
            const j = k % 10;
            return (
              <circle key={`d-${i}-${j}`} cx={100 + i * 92} cy={100 + j * 62} r="1.2" />
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/** Right-side abstract “product” stack for the hero (dark glass UI). */
export function HeroIllustration() {
  return (
    <div className={styles.heroIllustrationWrap} aria-hidden="true">
      <svg
        viewBox="0 0 440 400"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.heroIllustrationSvg}
      >
        <defs>
          <linearGradient id="hmain-panel" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2a2b32" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#1a1b22" stopOpacity="0.92" />
          </linearGradient>
          <linearGradient id="hmain-bar" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#edc917" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#edc917" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <g opacity="0.45" transform="translate(24 28) rotate(-6 200 200)">
          <rect x="0" y="0" width="320" height="220" rx="14" fill="#15161c" stroke="#fff" strokeOpacity="0.06" />
        </g>
        <g transform="translate(48 52)">
          <rect x="0" y="0" width="340" height="236" rx="14" fill="url(#hmain-panel)" stroke="#fff" strokeOpacity="0.1" />
          <rect x="0" y="0" width="340" height="44" rx="14" fill="#121318" fillOpacity="0.85" />
          <rect x="0" y="30" width="340" height="14" fill="#121318" fillOpacity="0.85" />
          <circle cx="28" cy="22" r="5" fill="#edc917" fillOpacity="0.85" />
          <rect x="48" y="16" width="88" height="8" rx="2" fill="#fff" fillOpacity="0.2" />
          <rect x="48" y="30" width="140" height="6" rx="2" fill="#fff" fillOpacity="0.1" />
          <rect x="20" y="64" width="140" height="72" rx="8" fill="#0d0e12" stroke="#fff" strokeOpacity="0.06" />
          <rect x="28" y="118" width="36" height="10" rx="2" fill="url(#hmain-bar)" />
          <rect x="28" y="98" width="52" height="6" rx="2" fill="#fff" fillOpacity="0.12" />
          <rect x="180" y="64" width="140" height="72" rx="8" fill="#0d0e12" stroke="#fff" strokeOpacity="0.06" />
          {[0, 1, 2, 3, 4].map((n) => (
            <rect
              key={`bar-${n}`}
              x={188 + n * 22}
              y={118 - n * 10}
              width="14"
              height={20 + n * 10}
              rx="3"
              fill="#edc917"
              fillOpacity={0.15 + n * 0.12}
            />
          ))}
          <rect x="20" y="152" width="300" height="1" fill="#fff" fillOpacity="0.06" />
          <rect x="20" y="168" width="200" height="8" rx="2" fill="#fff" fillOpacity="0.08" />
          <rect x="20" y="184" width="160" height="8" rx="2" fill="#fff" fillOpacity="0.06" />
          <rect x="20" y="200" width="120" height="8" rx="2" fill="#fff" fillOpacity="0.05" />
          <rect x="248" y="168" width="72" height="36" rx="8" fill="#edc917" fillOpacity="0.22" />
        </g>
        <g transform="translate(12 268)" opacity="0.55">
          <rect x="0" y="0" width="300" height="96" rx="12" fill="#1e1f26" stroke="#fff" strokeOpacity="0.06" />
        </g>
        <g transform="translate(20 276)">
          <rect x="0" y="0" width="284" height="88" rx="12" fill="#25262e" stroke="#fff" strokeOpacity="0.1" />
          <rect x="16" y="20" width="120" height="8" rx="2" fill="#fff" fillOpacity="0.15" />
          <rect x="16" y="36" width="180" height="6" rx="2" fill="#fff" fillOpacity="0.08" />
          <rect x="16" y="52" width="100" height="24" rx="6" fill="#edc917" fillOpacity="0.35" />
          <rect x="200" y="44" width="68" height="28" rx="8" fill="#fff" fillOpacity="0.06" />
        </g>
      </svg>
    </div>
  );
}

/** Right-column abstract graphic for About (Stripe-style illustration). */
export function AboutIllustration() {
  return (
    <div className={styles.aboutIllustration} aria-hidden="true">
      <svg viewBox="0 0 360 420" xmlns="http://www.w3.org/2000/svg" className={styles.aboutSvg}>
        <defs>
          <linearGradient id="aboutCard" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="100%" stopColor="#e8eaef" />
          </linearGradient>
          <linearGradient id="aboutAccent" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#edc917" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#edc917" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        <rect x="40" y="48" width="280" height="180" rx="12" fill="url(#aboutCard)" stroke="#d5d8de" strokeWidth="1" />
        <rect x="64" y="80" width="120" height="10" rx="3" fill="#c5c8ce" fillOpacity="0.5" />
        <rect x="64" y="104" width="200" height="8" rx="2" fill="#c5c8ce" fillOpacity="0.35" />
        <rect x="64" y="124" width="160" height="8" rx="2" fill="#c5c8ce" fillOpacity="0.35" />
        <rect x="64" y="168" width="72" height="36" rx="6" fill="url(#aboutAccent)" />
        <circle cx="260" cy="186" r="36" fill="#47494b" fillOpacity="0.08" />
        <circle cx="260" cy="186" r="22" fill="#edc917" fillOpacity="0.25" />
        <path
          d="M48 280 L 180 240 L 312 268 L 312 300 L 48 300 Z"
          fill="#47494b"
          fillOpacity="0.06"
        />
        <path
          d="M48 292 L 120 262 L 200 272 L 312 252"
          fill="none"
          stroke="#47494b"
          strokeOpacity="0.2"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="120" cy="262" r="4" fill="#edc917" fillOpacity="0.85" />
        <circle cx="200" cy="272" r="4" fill="#47494b" fillOpacity="0.35" />
        <circle cx="280" cy="258" r="4" fill="#47494b" fillOpacity="0.2" />
        <rect x="72" y="320" width="216" height="56" rx="8" fill="#fff" stroke="#d5d8de" strokeWidth="1" />
        <rect x="92" y="340" width="100" height="8" rx="2" fill="#c5c8ce" fillOpacity="0.45" />
        <rect x="92" y="356" width="64" height="6" rx="2" fill="#c5c8ce" fillOpacity="0.3" />
      </svg>
    </div>
  );
}

/** Dark section ambient graphic behind M3 copy. */
export function M3Backdrop() {
  return (
    <div className={styles.m3Backdrop} aria-hidden="true">
      <svg
        className={styles.m3Svg}
        viewBox="0 0 1200 600"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="m3Glow" cx="75%" cy="20%" r="55%">
            <stop offset="0%" stopColor="#edc917" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="1200" height="600" fill="url(#m3Glow)" />
        <g stroke="#fff" strokeOpacity="0.05" fill="none" strokeWidth="1">
          <path d="M100 480 L 280 320 L 460 400 L 640 280 L 820 360 L 1100 220" />
          <path d="M100 520 L 300 360 L 500 440 L 700 300 L 900 380 L 1100 280" />
        </g>
        <g fill="#edc917" fillOpacity="0.12">
          <circle cx="280" cy="320" r="3" />
          <circle cx="640" cy="280" r="3" />
          <circle cx="900" cy="380" r="3" />
        </g>
      </svg>
    </div>
  );
}
